import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'camption_wishlist_v1';

const readStorage = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch (_) {
    return [];
  }
};

const writeStorage = (ids) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    window.dispatchEvent(new CustomEvent('camption-wishlist-change', { detail: ids }));
  } catch (_) { /* ignore */ }
};

/**
 * localStorage-based tour wishlist.
 * Broadcasts changes across components via a custom event so any TourCard heart
 * stays in sync when toggled elsewhere.
 */
export const useWishlist = () => {
  const [ids, setIds] = useState(readStorage);

  useEffect(() => {
    const handler = (e) => {
      const next = (e && e.detail) || readStorage();
      setIds(Array.isArray(next) ? next : readStorage());
    };
    window.addEventListener('camption-wishlist-change', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('camption-wishlist-change', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const has = useCallback((id) => ids.includes(id), [ids]);

  const toggle = useCallback((id) => {
    if (!id) return;
    const cur = readStorage();
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    writeStorage(next);
    setIds(next);
  }, []);

  const remove = useCallback((id) => {
    const cur = readStorage().filter((x) => x !== id);
    writeStorage(cur);
    setIds(cur);
  }, []);

  const clear = useCallback(() => {
    writeStorage([]);
    setIds([]);
  }, []);

  return { ids, count: ids.length, has, toggle, remove, clear };
};
